import { ActionIconButton } from './ui/ActionButton';
import { ReadinessThresholds } from './ReadinessThresholds';
import { displayDigits } from '../utils/displayDigits';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { IconType } from 'react-icons';
import {
  IoBatteryHalfOutline,
  IoCloseOutline,
  IoFitnessOutline,
  IoHeartOutline,
  IoInformationCircleOutline,
  IoMoonOutline,
  IoPulseOutline,
} from 'react-icons/io5';
import type { ReadinessToday, TodayTrends } from '../services/api';
import { GradientGauge } from './GradientGauge';

// ─── Level config ─────────────────────────────────────────────────────────────

const LEVEL_CONFIG = {
  optimal: { color: 'var(--text-success)', bg: 'rgba(60,198,125,0.12)', border: 'rgba(60,198,125,0.25)', labelKey: 'readiness.optimal' },
  good:    { color: 'var(--text-info)', bg: 'rgba(41,212,198,0.12)', border: 'rgba(41,212,198,0.25)', labelKey: 'readiness.good' },
  fair:    { color: 'var(--text-warning)', bg: 'rgba(245,183,64,0.12)', border: 'rgba(245,183,64,0.25)', labelKey: 'readiness.fair' },
  poor:    { color: 'var(--text-danger)', bg: 'color-mix(in srgb, var(--text-danger) 12%, transparent)', border: 'color-mix(in srgb, var(--text-danger) 25%, transparent)', labelKey: 'readiness.poor' },
};

const FACTOR_META: Record<keyof ReadinessToday['factors'], { labelKey: string; unit?: string; Icon: IconType; tone: string }> = {
  tsb:      { labelKey: 'readiness.factors.tsb', Icon: IoPulseOutline, tone: '#7B68EE' },
  hrv:      { labelKey: 'readiness.factors.hrv', unit: 'ms', Icon: IoHeartOutline, tone: 'var(--text-accent)' },
  sleep:    { labelKey: 'readiness.factors.sleep', Icon: IoMoonOutline, tone: '#448AFF' },
  fatigue:  { labelKey: 'readiness.factors.fatigue', Icon: IoBatteryHalfOutline, tone: '#F5B740' },
  soreness: { labelKey: 'readiness.factors.soreness', Icon: IoFitnessOutline, tone: '#29D4C6' },
};

const FACTOR_ORDER: Array<keyof ReadinessToday['factors']> = ['tsb', 'hrv', 'sleep', 'fatigue', 'soreness'];

// ─── Triptych tile ────────────────────────────────────────────────────────────

function TriptychTile({ label, value, valueColor, tone, onClick }: {
  onClick?: () => void;
  label: string; value: string | number; valueColor: string; tone?: 'ctl' | 'atl' | 'tsb';
}) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag type={onClick ? 'button' : undefined} onClick={onClick} data-metric={tone} className={`readiness-card__metric${Number(value) < 0 ? ' readiness-card__metric--negative' : ''}`} style={{
      border: 0, color: 'inherit', font: 'inherit', textAlign: 'inherit', cursor: onClick ? 'pointer' : undefined, width: '100%', flex: 1, padding: '8px 10px', borderRadius: 8,
      background: 'var(--surface-sunken)',
      display: 'flex', flexDirection: 'column', gap: 2,
    }}>
      <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--fg-4)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </span>
      <span style={{ fontSize: 18, fontWeight: 800, color: valueColor, fontFamily: 'var(--font-display)', lineHeight: 1.1 }}>
        {displayDigits(value)}
      </span>
    </Tag>
  );
}

function ReadinessUnavailableGauge({ size, label }: { size: number; label: string }) {
  const { t } = useTranslation();
  return (
    <div role="status" aria-label={label}
      style={{ width: size, height: size, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <img src="/readiness-wearables.png?v=1" alt="" aria-hidden="true"
        style={{ width: '100%', height: 'calc(100% - 24px)', objectFit: 'contain', position: 'relative' }} />
      <span style={{ position: 'relative', fontSize: 10, fontWeight: 700, color: 'var(--fg-3)', textAlign: 'center', lineHeight: '24px', whiteSpace: 'nowrap' }}>{t('readiness.connectWatch')}</span>
    </div>
  );
}

function ReadinessGuideModal({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="readiness-guide-modal__overlay animate-fadein"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="readiness-guide-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="readiness-guide-title"
      >
        <div className="readiness-guide-modal__header">
          <h2 id="readiness-guide-title" className="readiness-guide-modal__title">
            {t('readiness.guide.title')}
          </h2>
          <ActionIconButton
            type="button"
            className="readiness-guide-modal__close"
            onClick={onClose}
            label={t('readiness.guide.close')}
            icon={<IoCloseOutline aria-hidden="true" />}
          />
        </div>

        {children}
        <ul className="readiness-guide-modal__list">
          {FACTOR_ORDER.map((key) => {
            const meta = FACTOR_META[key];
            const Icon = meta.Icon;
            return (
              <li className="readiness-guide-modal__item" key={key}>
                <span
                  className="readiness-guide-modal__icon"
                  style={{ color: meta.tone, background: `${meta.tone}18` }}
                >
                  <Icon aria-hidden="true" />
                </span>
                <span className="readiness-guide-modal__label">{t(meta.labelKey)}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>,
    document.body,
  );
}

// ─── Main ReadinessCard ───────────────────────────────────────────────────────

interface ReadinessCardProps {
  athleteId?: number | null;
  onOpenPmc?: () => void;
  today: ReadinessToday;
  trends: TodayTrends | null;
}

export function ReadinessCard({ today, trends, athleteId, onOpenPmc }: ReadinessCardProps) {
  const { t, i18n } = useTranslation();
  const [guideOpen, setGuideOpen] = useState(false);
  const cfg = LEVEL_CONFIG[today.level] ?? LEVEL_CONFIG.fair;
  const hasCompleteInputs = FACTOR_ORDER.every((key) => today.factors[key]?.available === true);
  const missingFactorLabels = FACTOR_ORDER
    .filter((key) => today.factors[key]?.available !== true)
    .map((key) => t(FACTOR_META[key].labelKey));

  const tsbNum = trends?.tsb != null ? Number(trends.tsb) : null;
  const formColor = tsbNum != null ? (tsbNum > 5 ? 'var(--text-success)' : tsbNum < -10 ? 'var(--text-danger)' : 'var(--fg-3)') : 'var(--fg-4)';

  return (
    <div className="readiness-card" style={{
      display: 'flex', flexDirection: 'column', gap: 12,
      padding: 16, borderRadius: 'var(--r-4)',
      background: 'var(--surface-0)',
      boxShadow: 'var(--nm-raised)',
      minWidth: 0,
      maxWidth: '100%',
      boxSizing: 'border-box',
      overflow: 'hidden',
    }}>
      {/* Header */}
      {athleteId != null && <ReadinessThresholds key={athleteId} athleteId={athleteId} />}

      {/* Gauge + ATL/CTL/TSB */}
      <div className="readiness-card__content" style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0, flexWrap: 'wrap' }}>
        <div className="readiness-gauge-shell" style={{ flexShrink: 0 }}>
          <ActionIconButton
            type="button"
            className="readiness-guide__trigger"
            title={t('readiness.guide.open')}
            onClick={() => setGuideOpen(true)}
            label={t('readiness.guide.open')}
            icon={<IoInformationCircleOutline aria-hidden="true" />}
          />

          {hasCompleteInputs ? (
            <GradientGauge score={today.score} size={130} scoreLabel={t('readiness.scoreLabel')} />
          ) : (
            <ReadinessUnavailableGauge size={130} label={t('readiness.insufficientData')} />
          )}
          {hasCompleteInputs && <span style={{ display:'block', textAlign:'center', color:cfg.color, fontWeight:700, fontSize:11 }}>{t(cfg.labelKey)}</span>}
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <TriptychTile onClick={onOpenPmc} tone="atl" label={t('readiness.atl')} value={trends?.atl != null ? Math.round(trends.atl) : '—'} valueColor="#F5B740" />
          <TriptychTile onClick={onOpenPmc} tone="ctl" label={t('readiness.ctl')} value={trends?.ctl != null ? Math.round(trends.ctl) : '—'} valueColor="var(--chart-pmc-ctl)" />
          <TriptychTile onClick={onOpenPmc} tone="tsb"
            label={i18n.resolvedLanguage?.startsWith('fa') ? 'فرم (TSB)' : t('readiness.tsb')}
            value={tsbNum != null ? Math.round(tsbNum) : '—'}
            valueColor={formColor}
          />
        </div>
      </div>



      <ReadinessGuideModal open={guideOpen} onClose={() => setGuideOpen(false)}>
        {!hasCompleteInputs && <p style={{ color: 'var(--fg-2)', lineHeight: 1.9 }}>{t('readiness.connectWatchHint')}</p>}
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 3,
        fontSize: 11, color: 'var(--fg-3)',
        textAlign: 'center', padding: '5px 0',
        borderTop: '1px solid var(--surface-sunken)',
      }}>
        {hasCompleteInputs ? (
          <span>{t(`readiness.recommendations.${today.level}`)}</span>
        ) : (
          <span>{t('readiness.completeDataRequired')}</span>
        )}
        {hasCompleteInputs && today.methodology_version === 'readiness_v2' && (
          <small style={{ color: 'var(--fg-4)', fontStyle: 'normal' }}>
            {t('readiness.methodologyV2')}
            {today.coverage != null
              ? ` · ${t('readiness.dataCoverage', { percent: Math.round(today.coverage * 100) })}`
              : ''}
          </small>
        )}
        {!hasCompleteInputs && missingFactorLabels.length > 0 && (
          <small style={{ color: 'var(--fg-4)', fontStyle: 'normal' }}>
            {t('readiness.missingFactors', { factors: missingFactorLabels.join('، ') })}
          </small>
        )}
      </div>
      </ReadinessGuideModal>

    </div>
  );
}
